import { Link } from "react-router-dom";
import { HardDrive, TrendingUp, Package, ArrowUpCircle, Info, Check, X } from "lucide-react";
import { useSubscriptionLimits } from "../../hooks/useSubscriptionLimits";
import StorageUsageBar from "../../components/StorageUsageBar";
import QuotaWarning from "../../components/QuotaWarning";
import { SUBSCRIPTION_PLANS, STORAGE_ADDONS } from "../../config/subscriptionPlans";

export default function StorageDashboard() {
  const {
    usage,
    loading,
    userTier,
    addonStorageGB,
    getVideoStorageInfo,
    getFileStorageInfo,
    getPlanLimits,
    getUpgradeRecommendation,
    getAddonRecommendation,
  } = useSubscriptionLimits();

  const videoInfo = getVideoStorageInfo();
  const fileInfo = getFileStorageInfo();
  const planLimits = getPlanLimits();
  const upgradeRec = getUpgradeRecommendation();
  const addonRec = getAddonRecommendation();
  
  // Map legacy tier names to current tier names
  const tierMapping: Record<string, string> = {
    'pro': 'pro_monthly',
    'yearly': 'pro_yearly',
    'pro_plus': 'pro_plus_monthly'
  };
  
  const mappedTier = tierMapping[userTier] || userTier;
  const currentPlan = SUBSCRIPTION_PLANS.find((p) => p.tier === mappedTier);

  const formatSize = (sizeMB: number): string => {
    if (sizeMB >= 1024) {
      return `${(sizeMB / 1024).toFixed(1)} GB`;
    }
    return `${sizeMB.toFixed(0)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            การใช้งานพื้นที่จัดเก็บ
          </h1>
          <p className="text-gray-600 text-lg">
            ติดตามการใช้งานและจัดการพื้นที่จัดเก็บของคุณ
          </p>
        </div>

        {/* Current Plan Card */}
        <div className="bg-gradient-to-r from-blue-600 to-pink-600 rounded-3xl p-8 shadow-xl mb-8 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Package className="w-8 h-8" />
                <h2 className="text-3xl font-bold">{currentPlan?.name || 'Free Trial'}</h2>
              </div>
              <p className="text-white/90 text-lg">
                แพ็กเกจปัจจุบันของคุณ
              </p>
              {addonStorageGB > 0 && (
                <p className="text-white/80 text-sm mt-2">
                  + Storage Add-on: {addonStorageGB} GB
                </p>
              )}
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold mb-1">
                ฿{currentPlan?.price?.toLocaleString() || '0'}
              </div>
              <p className="text-white/90">{currentPlan?.period || '/ 14 วัน'}</p>
            </div>
          </div>
        </div>

        {/* Warnings */}
        {videoInfo && videoInfo.showWarning && (
          <QuotaWarning
            type={videoInfo.percentage >= 100 ? "hard" : "soft"}
            resourceType="video"
            usagePercentage={videoInfo.percentage}
            currentTier={userTier}
            nextTier={upgradeRec?.nextTier}
            upgradeMessage={upgradeRec?.message}
            showAddonOption={planLimits?.allowAddons}
          />
        )}

        {fileInfo && fileInfo.showWarning && (
          <QuotaWarning
            type={fileInfo.percentage >= 100 ? "hard" : "soft"}
            resourceType="file"
            usagePercentage={fileInfo.percentage}
            currentTier={userTier}
            nextTier={upgradeRec?.nextTier}
            upgradeMessage={upgradeRec?.message}
            showAddonOption={planLimits?.allowAddons}
          />
        )}

        {/* Storage Usage */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {videoInfo && (
            <StorageUsageBar
              label="พื้นที่วิดีโอ"
              used={videoInfo.used}
              total={videoInfo.total}
              showWarning={videoInfo.showWarning}
            />
          )}
          {fileInfo && (
            <StorageUsageBar
              label="พื้นที่ไฟล์เอกสาร"
              used={fileInfo.used}
              total={fileInfo.total}
              showWarning={fileInfo.showWarning}
            />
          )}
        </div>

        {/* Usage Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <HardDrive className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-800">คอร์สทั้งหมด</h3>
            </div>
            <p className="text-3xl font-bold text-gray-800">
              {usage?.courseCount || 0}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {planLimits?.maxCourses === null
                ? "ไม่จำกัด"
                : `สูงสุด ${planLimits?.maxCourses} คอร์ส`}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-pink-100 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-pink-600" />
              </div>
              <h3 className="font-semibold text-gray-800">นักเรียน</h3>
            </div>
            <p className="text-3xl font-bold text-gray-800">
              {usage?.studentCount || 0}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {planLimits?.maxStudents === null
                ? "ไม่จำกัด"
                : `สูงสุด ${planLimits?.maxStudents} คน`}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-800">วิดีโอ</h3>
            </div>
            <p className="text-3xl font-bold text-gray-800">
              {formatSize(usage?.videoStorageUsed || 0)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              จาก {formatSize(planLimits?.storageGB.videoStorage || 0)}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Package className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-800">ไฟล์เอกสาร</h3>
            </div>
            <p className="text-3xl font-bold text-gray-800">
              {formatSize(usage?.fileStorageUsed || 0)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              จาก {formatSize(planLimits?.storageGB.fileStorage || 0)}
            </p>
          </div>
        </div>

        {/* Plan Limits Info */}
        <div className="bg-white rounded-3xl p-8 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Info className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800">
              ข้อจำกัดของแพ็กเกจ {currentPlan?.name}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-700">จำนวนคอร์ส</span>
                <span className="font-semibold text-gray-800">
                  {planLimits?.maxCourses === null ? "ไม่จำกัด" : `${planLimits?.maxCourses} คอร์ส`}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-700">บทเรียนต่อคอร์ส</span>
                <span className="font-semibold text-gray-800">
                  {planLimits?.maxLessonsPerCourse === null ? "ไม่จำกัด" : `${planLimits?.maxLessonsPerCourse} บท`}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-700">จำนวนนักเรียน</span>
                <span className="font-semibold text-gray-800">
                  {planLimits?.maxStudents === null ? "ไม่จำกัด" : `${planLimits?.maxStudents} คน`}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-700">จำนวนผู้สอน</span>
                <span className="font-semibold text-gray-800">
                  {planLimits?.maxInstructors === null ? "ไม่จำกัด" : `${planLimits?.maxInstructors} คน`}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-700">ขนาดวิดีโอต่อไฟล์</span>
                <span className="font-semibold text-gray-800">
                  {formatSize(planLimits?.storageGB.maxVideoSize || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-700">ขนาดเอกสารต่อไฟล์</span>
                <span className="font-semibold text-gray-800">
                  {formatSize(planLimits?.storageGB.maxFileSize || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-700">อัปโหลดความเร็วสูง</span>
                <span className="font-semibold text-gray-800 flex items-center gap-2">
                  {planLimits?.highSpeedUpload ? (
                    <><Check className="w-5 h-5 text-green-600" /> รองรับ</>
                  ) : (
                    <><X className="w-5 h-5 text-red-600" /> ไม่รองรับ</>
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-700">ไม่หักเปอร์เซ็นต์รายได้</span>
                <span className="font-semibold text-gray-800 flex items-center gap-2">
                  {planLimits?.noRevenueCut ? (
                    <><Check className="w-5 h-5 text-green-600" /> รองรับ</>
                  ) : (
                    <><X className="w-5 h-5 text-red-600" /> ไม่รองรับ</>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade/Add-on Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Upgrade Card */}
          {upgradeRec && (
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <ArrowUpCircle className="w-8 h-8 text-blue-600" />
                <h3 className="text-2xl font-bold text-gray-800">อัปเกรดแพ็กเกจ</h3>
              </div>
              <p className="text-gray-700 mb-6">{upgradeRec.message}</p>
              <Link to="/pricing#packages">
                <button className="w-full bg-gradient-to-r from-blue-600 to-pink-600 text-white px-6 py-4 rounded-xl font-bold hover:shadow-xl transition-all">
                  ดูแพ็กเกจทั้งหมด
                </button>
              </Link>
            </div>
          )}

          {/* Add-on Card */}
          {planLimits?.allowAddons && (
            <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-3xl p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Package className="w-8 h-8 text-blue-600" />
                <h3 className="text-2xl font-bold text-gray-800">เพิ่มพื้นที่จัดเก็บ</h3>
              </div>
              <p className="text-gray-700 mb-6">
                เพิ่มพื้นที่จัดเก็บเพิ่มเติมสำหรับแพ็กเกจ Yearly
              </p>
              <Link to="/checkout/addon">
                <button className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-4 rounded-xl font-bold hover:shadow-xl transition-all">
                  ดู Storage Add-ons
                </button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
